import { produce } from 'immer'
import {
  RESET_SIGNUP_ERRORS,
  ResetSignupErrorsAction,
} from 'src/app/Signup/redux/signup.actions'
import { signupRouteProps } from 'src/navigation/routeProps'
import {
  POST_PHONE_VERIFICATION_FAILURE,
  POST_PHONE_VERIFICATION_INIT,
  POST_PHONE_VERIFICATION_SUCCESS,
  POST_RESEND_PHONE_VERIFICATION_CODE_SUCCESS,
  POST_SEND_PHONE_VERIFICATION_CODE_FAILURE,
  POST_SEND_PHONE_VERIFICATION_CODE_INIT,
  POST_USER_FAILURE,
  POST_USER_INIT,
  POST_USER_RESEND_VERIFICATION_FAILURE,
  POST_USER_RESEND_VERIFICATION_INIT,
  POST_USER_RESEND_VERIFICATION_SUCCESS,
  POST_USER_SUCCESS,
  POST_USER_VERIFICATION_FAILURE,
  POST_USER_VERIFICATION_INIT,
  POST_USER_VERIFICATION_SUCCESS,
  PostPhoneVerificationActions,
  PostResendPhoneVerificationCodeSuccessAction,
  PostSendPhoneVerificationCodeActions,
  PostUserActions,
  PostUserResendVerificationActions,
  PostUserVerificationActions,
  RESET_VERIFICATION_ERRORS,
  ResetVerificationErrorsAction,
} from 'src/redux/actions/auth.actions'
import { createBackgroundReducer } from 'src/redux/genericReducers'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface SignupState {
  isPostingUser: boolean
  isVerifyingUser: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  verificationEmailResent: boolean
  verificationPhoneResent: boolean
  verifyPhoneVerificationLoading: boolean
  resendVerificationTopLevelError?: string
  verifyEmailTopLevelError?: string
  verifyPhoneTopLevelError?: string
  userEmailVerified: boolean
}

export const initialState: SignupState = {
  isPostingUser: false,
  isVerifyingUser: false,
  verificationEmailResent: false,
  verificationPhoneResent: false,
  verifyPhoneVerificationLoading: false,
  userEmailVerified: false,
}

export type Actions =
  | PostUserActions
  | ResetSignupErrorsAction
  | PostUserVerificationActions
  | PostUserResendVerificationActions
  | PostSendPhoneVerificationCodeActions
  | PostPhoneVerificationActions
  | PostResendPhoneVerificationCodeSuccessAction
  | ResetVerificationErrorsAction

export const CoreSignupReducer = (
  state = initialState,
  action: Actions,
): SignupState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case POST_USER_INIT: {
        draft.isPostingUser = true
        draft.topLevelError = undefined
        return draft
      }

      case POST_USER_SUCCESS: {
        draft.isPostingUser = false
        return draft
      }

      case POST_USER_FAILURE: {
        draft.isPostingUser = false
        const errors = parseBackendErrors(action.payload)
        draft.topLevelError = errors.topLevelError
        draft.errorDetailMap = errors.errorDetailMap
        return draft
      }

      case RESET_SIGNUP_ERRORS: {
        draft.topLevelError = undefined
        draft.errorDetailMap = undefined
        return draft
      }

      // send email verification
      case POST_USER_VERIFICATION_INIT: {
        draft.isVerifyingUser = true
        draft.userEmailVerified = false
        draft.verifyEmailTopLevelError = undefined
        return draft
      }

      case POST_USER_VERIFICATION_SUCCESS: {
        draft.isVerifyingUser = false
        draft.userEmailVerified = true
        return draft
      }

      case POST_USER_VERIFICATION_FAILURE: {
        draft.isVerifyingUser = false
        draft.userEmailVerified = false
        const errors = parseBackendErrors(action.payload)
        draft.verifyEmailTopLevelError = errors.topLevelError
        return draft
      }

      // resend email verification
      case POST_USER_RESEND_VERIFICATION_INIT: {
        draft.verificationEmailResent = false
        draft.resendVerificationTopLevelError = undefined
        return draft
      }

      case POST_USER_RESEND_VERIFICATION_SUCCESS: {
        draft.verificationEmailResent = true
        draft.resendVerificationTopLevelError = undefined
        return draft
      }

      case POST_USER_RESEND_VERIFICATION_FAILURE: {
        const errors = parseBackendErrors(action.payload)
        draft.resendVerificationTopLevelError = errors.topLevelError
        return draft
      }

      // send phone verification code
      case POST_SEND_PHONE_VERIFICATION_CODE_INIT: {
        draft.verificationPhoneResent = false
        draft.resendVerificationTopLevelError = undefined
        return draft
      }

      case POST_RESEND_PHONE_VERIFICATION_CODE_SUCCESS: {
        draft.verificationPhoneResent = true
        draft.resendVerificationTopLevelError = undefined
        return draft
      }

      case POST_SEND_PHONE_VERIFICATION_CODE_FAILURE: {
        const errors = parseBackendErrors(action.payload)
        draft.resendVerificationTopLevelError = errors.topLevelError
        return draft
      }

      // verify phone verification code
      case POST_PHONE_VERIFICATION_INIT: {
        draft.verifyPhoneVerificationLoading = true
        draft.verifyPhoneTopLevelError = undefined
        return draft
      }

      case POST_PHONE_VERIFICATION_SUCCESS: {
        draft.verifyPhoneVerificationLoading = false
        draft.verifyPhoneTopLevelError = undefined
        return draft
      }

      case POST_PHONE_VERIFICATION_FAILURE: {
        draft.verifyPhoneVerificationLoading = false
        const errors = parseBackendErrors(action.payload)
        draft.verifyPhoneTopLevelError = errors.topLevelError
        return draft
      }

      case RESET_VERIFICATION_ERRORS: {
        draft.resendVerificationTopLevelError = undefined
        draft.verifyPhoneTopLevelError = undefined
        return draft
      }
    }
  })
}

const SignupReducer = createBackgroundReducer(
  signupRouteProps,
  initialState,
  CoreSignupReducer,
)

export default SignupReducer
